import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  MapPinned,
  Boxes,
  Sparkles,
  Star,
  Quote,
  Users,
  Award,
  Smile,
  CalendarClock,
} from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useReveal } from "../hooks/useReveal";
import { SectionHeader, PackageCard, AccentTitle } from "../components/Shared";
import { useContent } from "../context/ContentContext";
import { formatPrice } from "../lib/api";

const Counter = ({ target, suffix = "" }) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let start = 0;
    const step = target / 60;
    const id = setInterval(() => {
      start += step;
      if (start >= target) {
        start = target;
        clearInterval(id);
      }
      setVal(Math.floor(start));
    }, 25);
    return () => clearInterval(id);
  }, [target]);
  return (
    <span>
      {val.toLocaleString()}
      {suffix}
    </span>
  );
};

const Home = () => {
  const { t, lang } = useLang();
  const { IMAGES, PACKAGES, HALAL_TOURS, TESTIMONIALS, BLOGS, PARTNERS, settings } = useContent();
  const [slide, setSlide] = useState(0);
  useReveal([lang]);

  const slideCount = (IMAGES.heroSlides || []).length || 1;
  useEffect(() => {
    const id = setInterval(() => setSlide((s) => (s + 1) % slideCount), 5000);
    return () => clearInterval(id);
  }, [slideCount]);

  const featuredPacks = PACKAGES.filter((p) => p.featured).concat(
    PACKAGES.filter((p) => !p.featured)
  ).slice(0, 3);
  const heroPack = PACKAGES.find((p) => p.type === "hajj" && p.featured) || featuredPacks[0];

  const whyIcons = [ShieldCheck, MapPinned, Boxes, Sparkles];
  const stats = [
    { icon: Smile, value: settings.stats?.pilgrims ?? 1000, suffix: "+", label: t.stats.travelers },
    { icon: Award, value: settings.stats?.success ?? 100, suffix: "%", label: t.stats.success },
    { icon: Star, value: 5, suffix: "★", label: t.stats.rating },
    { icon: CalendarClock, value: settings.stats?.years ?? 5, suffix: "+", label: t.stats.experience },
  ];

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden" data-testid="home-hero">
        {IMAGES.heroSlides.map((img, i) => (
          <div
            key={i}
            className="absolute inset-0 bg-cover bg-center transition-opacity duration-[1500ms]"
            style={{ backgroundImage: `url(${img})`, opacity: i === slide ? 1 : 0, transform: i === slide ? "scale(1.06)" : "scale(1)", transition: "opacity 1.5s ease, transform 7s linear" }}
          />
        ))}
        <div className="absolute inset-0 hero-mesh" />
        <div className="absolute inset-0 star-pattern opacity-[0.12]" />
        <div className="blob w-[520px] h-[520px] bg-jannah-gold/40 -top-40 -right-20" />
        <div className="blob w-[420px] h-[420px] bg-jannah-green-light/50 bottom-0 left-1/3" style={{ animationDelay: "-6s" }} />

        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-36 w-full grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Link to="/hajj/pre-registration" className="inline-flex items-center gap-2.5 glass-dark rounded-full pl-2 pr-4 py-1.5 text-xs font-semibold text-white animate-fade-up hover:bg-white/15 transition-colors" data-testid="hero-prereg-pill">
              <span className="px-2.5 py-1 rounded-full btn-gold text-[10px] uppercase tracking-widest">Hajj 2027</span>
              <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-jannah-gold-light opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-jannah-gold" /></span>
              {lang === "en" ? "Pre-registration is open" : "প্রি-রেজিস্ট্রেশন চলছে"} <ArrowRight size={13} />
            </Link>
            <span className="block mt-6 eyebrow eyebrow-line text-jannah-gold-light animate-fade-up">{t.hero.eyebrow}</span>
            <h1
              className="mt-4 text-5xl md:text-7xl font-extrabold text-white leading-[1.02] tracking-tight animate-fade-up"
              style={{ animationDelay: "0.1s" }}
            >
              <AccentTitle text={t.hero.title} accentClass="text-shimmer" highlights={{ Hajj: "text-[#00BFFF]", "হজ্জ": "text-[#00BFFF]", "হজ": "text-[#00BFFF]", "হজ্জের": "text-[#00BFFF]" }} />
            </h1>
            <p
              className="mt-7 text-lg md:text-xl text-white/80 leading-relaxed max-w-xl animate-fade-up"
              style={{ animationDelay: "0.2s" }}
            >
              {t.hero.subtitle}
            </p>
            <div
              className="mt-10 flex flex-wrap items-center gap-4 animate-fade-up"
              style={{ animationDelay: "0.3s" }}
            >
              <Link
                to="/umrah"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full btn-gold font-bold"
                data-testid="hero-explore-btn"
              >
                {t.hero.cta1} <ArrowRight size={18} />
              </Link>
              <Link
                to="/customize"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full glass-dark text-white font-semibold hover:bg-white/15 transition-colors"
                data-testid="hero-custom-btn"
              >
                {t.nav.custom}
              </Link>
            </div>
          </div>

          <div className="hidden lg:block lg:col-span-5 relative">
            <div className="orbit w-[520px] h-[520px] -top-16 -right-10" />
            <div className="orbit w-[400px] h-[400px] top-0 right-6" style={{ animationDirection: "reverse", animationDuration: "45s" }} />
            <div className="relative glass rounded-[2rem] p-7 ml-auto max-w-sm shadow-lift animate-float" data-testid="hero-custom-card">
              <div className="flex items-center justify-between">
                <span className="eyebrow text-jannah-gold">{lang === "en" ? "Build your own" : "নিজের মতো সাজান"}</span>
                <span className="px-2.5 py-1 rounded-full bg-jannah-green/10 text-jannah-green-dark text-[11px] font-bold">{lang === "en" ? "Hajj · Umrah" : "হজ্জ · উমরাহ"}</span>
              </div>
              <h3 className="mt-3 text-2xl font-bold text-jannah-navy tracking-tight">{t.custom.title}</h3>
              <div className="mt-4 rounded-2xl overflow-hidden h-36"><img src={heroPack?.image || IMAGES.heroSlides[0]} alt="" className="w-full h-full object-cover" /></div>
              <ul className="mt-4 space-y-1.5 text-sm text-jannah-ink/75">
                {(lang === "en"
                  ? ["Choose your hotels, dates & duration", "Family & group friendly pricing", "Personal quote within 24 hours"]
                  : ["হোটেল, তারিখ ও সময়কাল নিজে বেছে নিন", "পরিবার ও গ্রুপের জন্য উপযোগী মূল্য", "২৪ ঘণ্টার মধ্যে ব্যক্তিগত কোটেশন"]
                ).map((x, i) => (
                  <li key={i} className="flex items-center gap-2"><ShieldCheck size={14} className="text-jannah-green" /> {x}</li>
                ))}
              </ul>
              <div className="mt-5 flex items-end justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{lang === "en" ? "Tailored for you" : "আপনার জন্য তৈরি"}</div>
                  <div className="text-2xl font-bold text-jannah-green-dark">{t.nav.custom}</div>
                </div>
                <Link to="/customize" className="w-12 h-12 rounded-full btn-gold flex items-center justify-center" aria-label={t.nav.custom} data-testid="hero-custom-link"><ArrowRight size={18} /></Link>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-28 left-1/2 -translate-x-1/2 flex gap-2">
          {IMAGES.heroSlides.map((_, i) => (
            <button
              key={i}
              onClick={() => setSlide(i)}
              aria-label={`Slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${
                i === slide ? "w-8 bg-jannah-gold" : "w-3 bg-white/50"
              }`}
            />
          ))}
        </div>
      </section>

      {/* QUICK LINKS (overlapping bento) */}
      <section className="relative z-10 -mt-24 max-w-7xl mx-auto px-6" data-testid="home-quicklinks">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { to: "/hajj", icon: Boxes, en: "Hajj 2027 Packages", bn: "হজ ২০২৭ প্যাকেজ", sub_en: "Category A & B", sub_bn: "ক্যাটাগরি A ও B" },
            { to: "/umrah", icon: MapPinned, en: "Umrah Packages", bn: "উমরাহ প্যাকেজ", sub_en: "Economy to Luxury", sub_bn: "ইকোনমি থেকে লাক্সারি" },
            { to: "/hajj/pre-registration", icon: CalendarClock, en: "Pre-Registration", bn: "প্রি-রেজিস্ট্রেশন", sub_en: "৳ 30,000 · 2 years valid", sub_bn: "৳ ৩০,০০০ · ২ বছর বৈধ" },
            { to: "/customize", icon: Sparkles, en: "Custom Package", bn: "কাস্টম প্যাকেজ", sub_en: "Built around you", sub_bn: "আপনার মতো করে" },
          ].map((q, i) => (
            <Link key={q.to} to={q.to} className="gold-tracer glass rounded-3xl p-6 flex items-start gap-4 hover:-translate-y-1.5 transition-transform duration-300 reveal" style={{ transitionDelay: `${i * 80}ms` }} data-testid={`quicklink-${q.to.replace(/\//g, "-").slice(1)}`}>
              <div className="w-12 h-12 rounded-2xl mesh-band text-jannah-gold-light flex items-center justify-center shrink-0"><q.icon size={22} /></div>
              <div>
                <div className="font-bold text-jannah-navy">{lang === "en" ? q.en : q.bn}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{lang === "en" ? q.sub_en : q.sub_bn}</div>
              </div>
              <ArrowRight size={16} className="ml-auto text-jannah-gold mt-1" />
            </Link>
          ))}
        </div>
      </section>

      {/* STATS */}
      <section className="relative pt-24 pb-8" data-testid="home-stats">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="reveal text-center rounded-3xl bg-white border border-jannah-sand p-7 shadow-ambient hover:shadow-lift transition-shadow">
                <Icon className="mx-auto mb-3 text-jannah-gold" size={26} />
                <div className="text-4xl md:text-5xl font-bold text-jannah-navy tracking-tight">
                  <Counter target={s.value} suffix={s.suffix} />
                </div>
                <p className="mt-1.5 text-sm text-muted-foreground">{s.label}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ABOUT PREVIEW */}
      <section className="section-pad bg-jannah-cream">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative reveal">
            <img
              src={IMAGES.aboutMain}
              alt="Pilgrims"
              className="rounded-2xl w-full h-[420px] object-cover shadow-xl"
            />
            <img
              src={IMAGES.aboutSecondary}
              alt="Kaaba"
              className="hidden md:block absolute -bottom-10 -right-6 w-52 h-64 object-cover rounded-2xl border-4 border-jannah-cream shadow-2xl animate-float"
            />
            <div className="absolute -top-6 -left-6 bg-jannah-gold text-white rounded-2xl px-6 py-5 shadow-xl">
              <div className="text-4xl font-bold">{settings.stats?.years ?? 5}+</div>
              <div className="text-xs font-semibold uppercase tracking-wide">
                {t.stats.experience}
              </div>
            </div>
          </div>

          <div className="reveal">
            <SectionHeader
              eyebrow={t.about.eyebrow}
              title={t.about.title}
              center={false}
            />
            <p className="text-muted-foreground leading-relaxed">{t.about.body}</p>
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              {t.about.points.map((p, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-jannah-green-dark">
                  <ShieldCheck size={18} className="text-jannah-gold shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
            <Link
              to="/about"
              className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-jannah-green text-white font-semibold hover:bg-jannah-green-dark"
            >
              {t.about.btn} <ArrowRight size={17} />
            </Link>
          </div>
        </div>
      </section>

      {/* PACKAGES */}
      <section className="section-pad bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="reveal">
            <SectionHeader
              eyebrow={t.sec.packages_eye}
              title={t.sec.packages_title}
              subtitle={t.sec.packages_sub}
            />
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredPacks.map((p) => (
              <div key={p.id} className="reveal">
                <PackageCard pkg={p} />
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/umrah"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border-2 border-jannah-green text-jannah-green font-semibold hover:bg-jannah-green hover:text-white"
            >
              {t.view_all} <ArrowRight size={17} />
            </Link>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="section-pad mesh-band relative overflow-hidden" data-testid="home-why">
        <div className="absolute inset-0 star-pattern opacity-[0.08]" />
        <div className="blob w-[500px] h-[500px] bg-jannah-gold/30 -bottom-40 -left-20" />
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="reveal max-w-2xl">
            <SectionHeader eyebrow={t.sec.why_eye} title={t.sec.why_title} subtitle={t.sec.why_sub} center={false} light />
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 lg:grid-rows-2 gap-5">
            <div className="reveal relative lg:col-span-2 lg:row-span-2 rounded-3xl overflow-hidden min-h-[320px] group">
              <img src={IMAGES.whyChoose} alt="Pilgrims praying" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[2s] group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-jannah-obsidian/90 via-jannah-obsidian/20 to-transparent" />
              <div className="absolute bottom-0 p-8">
                <span className="eyebrow text-jannah-gold-light">{settings.stats?.pilgrims ?? 1000}+ {t.stats.travelers}</span>
                <h3 className="mt-2 text-3xl font-bold text-white tracking-tight">{t.why[0].title}</h3>
                <p className="mt-2 text-white/75 text-sm max-w-md">{t.why[0].desc}</p>
              </div>
            </div>
            {t.why.slice(1).map((w, i) => {
              const Icon = whyIcons[i + 1];
              return (
                <div key={i} className={`reveal gold-tracer glass-dark rounded-3xl p-7 hover:bg-white/10 transition-colors ${i === 0 ? "lg:col-span-2" : ""}`}>
                  <div className="w-12 h-12 rounded-2xl bg-jannah-gold/15 border border-jannah-gold/30 flex items-center justify-center mb-5">
                    <Icon className="text-jannah-gold-light" size={22} />
                  </div>
                  <h3 className="text-white text-xl font-bold mb-2">{w.title}</h3>
                  <p className="text-white/70 text-sm leading-relaxed">{w.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HALAL TOURS */}
      <section className="section-pad bg-jannah-cream">
        <div className="max-w-7xl mx-auto px-6">
          <div className="reveal">
            <SectionHeader
              eyebrow={t.sec.halal_eye}
              title={t.sec.halal_title}
              subtitle={t.sec.halal_sub}
            />
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {HALAL_TOURS.slice(0, 3).map((tour) => (
              <Link
                to="/halal-tours"
                key={tour.id}
                className="reveal card-hover group relative rounded-2xl overflow-hidden h-72 block"
              >
                <img
                  src={tour.image}
                  alt={lang === "en" ? tour.name_en : tour.name_bn}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-jannah-green-dark/90 via-jannah-green-dark/20 to-transparent" />
                <div className="absolute bottom-0 p-6 text-white">
                  <span className="px-3 py-1 rounded-full btn-gold text-xs font-bold">
                    ${tour.price} · {tour.duration} {t.days}
                  </span>
                  <h3 className="mt-3 text-2xl font-bold">
                    {lang === "en" ? tour.name_en : tour.name_bn}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/halal-tours"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border-2 border-jannah-green text-jannah-green font-semibold hover:bg-jannah-green hover:text-white"
            >
              {t.view_all} <ArrowRight size={17} />
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section-pad bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="reveal">
            <SectionHeader
              eyebrow={t.sec.testi_eye}
              title={t.sec.testi_title}
              subtitle={t.sec.testi_sub}
            />
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {TESTIMONIALS.map((r) => (
              <div
                key={r.id}
                className="reveal bg-jannah-cream rounded-2xl p-8 border border-jannah-sand relative"
              >
                <Quote className="text-jannah-gold/40 absolute top-6 right-6" size={44} />
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: r.rating }).map((_, i) => (
                    <Star key={i} size={17} className="fill-jannah-gold text-jannah-gold" />
                  ))}
                </div>
                <p className="text-jannah-green-dark/80 leading-relaxed italic">
                  “{lang === "en" ? r.text_en : r.text_bn}”
                </p>
                <div className="mt-6 flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-jannah-green text-white flex items-center justify-center font-bold text-lg">
                    {r.name.charAt(0)}
                  </div>
                  <div>
                    <div className="font-semibold text-jannah-green-dark">{r.name}</div>
                    <div className="text-sm text-muted-foreground">
                      {lang === "en" ? r.location_en : r.location_bn}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BLOG PREVIEW */}
      <section className="section-pad bg-jannah-cream">
        <div className="max-w-7xl mx-auto px-6">
          <div className="reveal">
            <SectionHeader
              eyebrow={t.sec.blog_eye}
              title={t.sec.blog_title}
              subtitle={t.sec.blog_sub}
            />
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {BLOGS.map((b) => (
              <Link
                to={`/blog/${b.slug}`}
                key={b.id}
                className="reveal card-hover bg-white rounded-2xl overflow-hidden border border-jannah-sand block"
              >
                <div className="h-52 overflow-hidden">
                  <img
                    src={b.image}
                    alt={lang === "en" ? b.title_en : b.title_bn}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <span className="text-xs font-semibold text-jannah-gold uppercase tracking-wide">
                    {lang === "en" ? b.category_en : b.category_bn}
                  </span>
                  <h3 className="mt-2 text-xl font-bold text-jannah-green-dark leading-snug">
                    {lang === "en" ? b.title_en : b.title_bn}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                    {lang === "en" ? b.excerpt_en : b.excerpt_bn}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-jannah-green font-semibold text-sm">
                    {t.read_more} <ArrowRight size={15} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERS */}
      <section className="py-12 bg-white border-y border-jannah-sand overflow-hidden">
        <p className="text-center eyebrow text-jannah-gold mb-8">{t.sec.affiliations}</p>
        <div className="relative">
          <div className="marquee gap-16 px-8">
            {[...PARTNERS, ...PARTNERS].map((p, i) => (
              <span
                key={i}
                className="text-2xl font-bold text-jannah-green/40 whitespace-nowrap"
              >
                {p.logo ? <img src={p.logo} alt={p.name} className="h-10 w-auto object-contain opacity-70" /> : p.name}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMAGES.ctaBg})` }}
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <div className="gold-divider mx-auto mb-6" />
          <h2 className="text-4xl md:text-5xl font-bold text-white">{t.cta.title}</h2>
          <p className="mt-5 text-white/80 leading-relaxed">{t.cta.body}</p>
          <Link
            to="/customize"
            className="mt-8 inline-flex items-center gap-2 px-8 py-4 rounded-full btn-gold font-semibold"
          >
            {t.cta.btn} <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
